import React from 'react';
import { PenTool } from 'lucide-react';
import { SKETCHPLAN_FEATURES } from '../constants';
import { sketchplanPricingCopy, SKETCHPLAN_PRICING_COPY } from '@rehub/pricing';
import DirectCardPricingTable from './DirectCardPricingTable';
import { useSketchplanCurrency } from './useSketchplanCurrency';

interface LandingPageProps {
  onOrder: () => void;
  onLogin?: () => void;
  onOpenPricing?: () => void;
}

const LandingPage: React.FC<LandingPageProps> = ({ onOrder, onOpenPricing }) => {
  const [currency] = useSketchplanCurrency();
  const pricing = sketchplanPricingCopy(currency);
  return (
    <main className="relative z-10">
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#40E0D0]/20 text-[#40E0D0] rounded-full text-sm font-medium mb-8">
            <PenTool size={14} className="shrink-0" />
            Sketchplan
          </div>
          <div className="flex justify-center mb-6">
            <img
              src="/cropped-Sketchplan-Logo1-removebg-preview.webp"
              alt="Sketchplan"
              className="h-20 md:h-24 w-auto object-contain drop-shadow-lg"
            />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight text-white">
            Turn your sketch into a professional floor plan
          </h1>
          <p className="text-xl text-slate-300 mb-6 max-w-2xl mx-auto">
            Upload your hand-drawn sketch, measured notes or existing layout. Our experienced team will redraw it into a clear, professional floor plan for property listings, brochures and marketing.
          </p>
          <p className="text-2xl md:text-3xl text-teal-300 font-bold mb-2">{pricing.headline}</p>
          <p className="text-base text-teal-100/90 mb-2">
            {pricing.supporting} {pricing.fromSize}
          </p>
          <p className="text-sm text-teal-200/80 mb-4">
            {pricing.commercialFromLine} {SKETCHPLAN_PRICING_COPY.deliveryStandard}. {SKETCHPLAN_PRICING_COPY.amendment}
          </p>
          <p className="text-sm text-slate-400 mb-10 max-w-xl mx-auto">
            {SKETCHPLAN_PRICING_COPY.noLogin}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <button
              onClick={onOrder}
              className="group flex items-center gap-3 px-10 py-5 bg-teal-600 hover:bg-teal-500 text-white rounded-2xl font-semibold text-lg transition-all shadow-xl shadow-teal-900/40 hover:scale-105"
            >
              Start My Floor Plan
            </button>
          </div>
          {onOpenPricing && (
            <button
              type="button"
              onClick={onOpenPricing}
              className="mt-4 text-sm font-medium text-teal-300 hover:text-teal-200 underline underline-offset-4"
            >
              View Pricing
            </button>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16 text-left">
            {SKETCHPLAN_FEATURES.slice(0, 3).map((feature) => (
              <div
                key={feature.title}
                className="p-6 bg-slate-900/70 backdrop-blur-md border border-slate-700/80 rounded-2xl"
              >
                <h3 className="text-lg font-semibold text-slate-100 mb-2">{feature.title}</h3>
                <p className="text-sm text-slate-400">{feature.description}</p>
              </div>
            ))}
          </div>

          <section className="mt-16 text-left">
            <DirectCardPricingTable onOrder={onOrder} />
          </section>
        </div>
      </div>
    </main>
  );
};

export default LandingPage;
